import { useState, useEffect } from "react";
import { useAuth } from "../App";
import axios from "axios";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../components/ui/select";
import {
  FileText,
  Download,
  TreeDeciduous,
  Loader2,
  FileDown,
  ClipboardList
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReportsPage = () => {
  const { user } = useAuth();
  const [trees, setTrees] = useState([]);
  const [selectedTree, setSelectedTree] = useState("");
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const isAgronomist = user?.role === "agronomist";

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchTrees();
  }, [user]);

  const fetchTrees = async () => {
    try {
      const endpoint = isAgronomist ? "/trees/all" : "/trees";
      const response = await axios.get(`${API}${endpoint}`, {
        withCredentials: true
      });
      setTrees(response.data);
    } catch (error) {
      console.error("Error fetching trees:", error);
      toast.error("Erro ao carregar árvores");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!selectedTree) {
      toast.error("Selecione uma árvore");
      return;
    }

    setGenerating(true);

    try {
      const response = await axios.get(`${API}/report/pdf/${selectedTree}`, {
        withCredentials: true,
        responseType: "blob"
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const tree = trees.find((t) => t.tree_id === selectedTree);
      link.setAttribute(
        "download",
        `relatorio_${tree?.name?.replace(/\s/g, "_") || "arvore"}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Relatório baixado com sucesso!");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Erro ao gerar relatório PDF");
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadAllPDF = async () => {
    setGenerating(true);

    try {
      const response = await axios.get(`${API}/report/pdf/all`, {
        withCredentials: true,
        responseType: "blob"
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `relatorio_geral.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Relatório geral baixado com sucesso!");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Erro ao gerar relatório geral");
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 md:p-8 max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8 animate-fade-in">
          <h1 className="text-2xl md:text-3xl font-bold font-['Outfit'] mb-2">
            Relatórios Técnicos
          </h1>
          <p className="text-muted-foreground">
            Gere relatórios PDF profissionais para suas árvores
          </p>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Select Tree & Actions */}
          <Card className="rounded-2xl card-shadow animate-fade-in">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <FileText className="w-5 h-5 text-primary" />
                Gerar Relatório Individual
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">
                  Selecione a Árvore
                </label>
                <Select value={selectedTree} onValueChange={setSelectedTree}>
                  <SelectTrigger data-testid="select-tree-report">
                    <SelectValue placeholder="Escolha uma árvore..." />
                  </SelectTrigger>
                  <SelectContent>
                    {trees.map((tree) => (
                      <SelectItem key={tree.tree_id} value={tree.tree_id}>
                        <div className="flex items-center gap-2">
                          <TreeDeciduous className="w-4 h-4 text-primary" />
                          {tree.name} - {tree.species}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="pt-4">
                <Button
                  data-testid="download-pdf-button"
                  onClick={handleDownloadPDF}
                  disabled={!selectedTree || generating}
                  className="w-full btn-primary"
                >
                  {generating ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <FileDown className="w-4 h-4 mr-2" />
                  )}
                  {generating ? "Gerando..." : "Baixar PDF"}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Agronomist Report */}
          {isAgronomist && (
            <Card className="rounded-2xl card-shadow animate-fade-in stagger-1">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <ClipboardList className="w-5 h-5 text-primary" />
                  Relatório Geral
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground text-sm">
                  Gere um relatório completo com todas as árvores cadastradas no
                  sistema, estatísticas de manutenção e inventário geral.
                </p>

                <div className="p-4 rounded-xl bg-accent/50 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Total de Árvores:</span>
                    <Badge variant="secondary">{trees.length}</Badge>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Espécies:</span>
                    <Badge variant="secondary">
                      {new Set(trees.map((t) => t.species)).size}
                    </Badge>
                  </div>
                </div>

                <Button
                  data-testid="download-all-pdf-button"
                  onClick={handleDownloadAllPDF}
                  disabled={generating}
                  className="w-full btn-primary"
                >
                  {generating ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  {generating ? "Gerando..." : "Baixar Relatório Geral"}
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Empty State */}
        {trees.length === 0 && (
          <Card className="mt-6 rounded-2xl card-shadow">
            <CardContent className="py-12 text-center">
              <TreeDeciduous className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Nenhuma árvore cadastrada</h3>
              <p className="text-muted-foreground">
                Cadastre suas árvores para gerar relatórios técnicos
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default ReportsPage;
